import { usePlan } from "@/contexts/PlanContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut, Sparkles } from "lucide-react";
import TileSphereLogo from "@/components/TileSphereLogo";

export default function SubscriptionGate({ children }) {
  const { plan, loading } = usePlan();
  const { logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return null;
  if (!plan || plan.status === "ACTIVE" || plan.status === "TRIALING") return children;

  const isExpired = plan.status === "EXPIRED" || plan.status === "CANCELLED" || plan.status === "NONE" || plan.status === "REJECTED";
  if (!isExpired) return children;

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white relative overflow-hidden grid place-items-center p-6" data-testid="subscription-expired-page">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-orange-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-fuchsia-600/15 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-2xl w-full">
        <div className="flex items-center justify-between mb-8">
          <TileSphereLogo />
          <button
            onClick={async () => { const p = await logout(); navigate(p || "/login"); }}
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
            data-testid="expired-logout"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-10 shadow-2xl shadow-black/40">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-[11px] font-bold uppercase tracking-[0.25em] text-red-300">
            <AlertTriangle className="w-3 h-3" /> Subscription · {plan.status || "Inactive"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-5 leading-[1]">
            Your TileSphere<br />
            <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">subscription has lapsed.</span>
          </h1>
          <p className="text-white/60 mt-5 text-lg max-w-md">
            Access to {plan.tenant_name || "your workspace"} is paused. Renew or upgrade your plan to restore your team.
          </p>

          <div className="mt-8 p-5 rounded-2xl bg-black/30 border border-white/10 grid grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-bold">Plan</div>
              <div className="font-display text-xl font-black mt-1">{plan.plan_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-bold">Status</div>
              <div className="font-display text-xl font-black mt-1 text-red-300">{plan.status}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-bold">Ended</div>
              <div className="font-mono text-xs mt-2 text-white/70">{plan.current_period_ends ? new Date(plan.current_period_ends).toLocaleDateString() : "—"}</div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={plan.upgrade_url || "/portals/organization"} data-testid="expired-renew-btn">
              <Button className="bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white font-bold rounded-xl h-11 px-6">
                <Sparkles className="w-4 h-4 mr-2" /> Renew / Upgrade plan
              </Button>
            </Link>
            <a href="mailto:billing@tilesphere.com" className="inline-block">
              <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 h-11 px-6">
                Contact billing
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
